import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StudentService } from 'src/app/service/student.service';

export interface StudentTransferDialogData {
  studentCourseApplicationId: number;
  studentName: string;
}

@Component({
  selector: 'app-student-transfer',
  templateUrl: './student-transfer.component.html',
  styleUrls: ['./student-transfer.component.scss']
})
export class StudentTransferComponent implements OnInit, OnDestroy {

  transferForm: FormGroup;
  institutes: any[] = [];
  courses: any[] = [];
  batches: any[] = [];
  destroyed$ = new Subject();

  constructor(
    public dialogRef: MatDialogRef<StudentTransferComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StudentTransferDialogData,
    private fb: FormBuilder,
    private studentService: StudentService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.transferForm = this.fb.group({
      toInstituteId: ['', Validators.required],
      toCourseId: ['', Validators.required],
      toBatchId: ['', Validators.required],
      reason: ['', Validators.required]
    });

    this.loadInstitutes();

    this.transferForm.get('toInstituteId').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(instituteId => this.onInstituteChange(instituteId));

    this.transferForm.get('toCourseId').valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(courseId => this.onCourseChange(courseId));
  }

  loadInstitutes(): void {
    const companyId = sessionStorage.getItem("companyId") ? JSON.parse(sessionStorage.getItem("companyId")) : null;
    if (companyId) {
      this.studentService.getTransferInstitutes(companyId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((res: any) => {
          this.institutes = res || [];
        });
    }
  }

  onInstituteChange(instituteId: any): void {
    this.transferForm.patchValue({ toCourseId: '', toBatchId: '' });
    this.courses = [];
    this.batches = [];
    const id = Number(instituteId);
    if (id) {
      this.studentService.getTransferCourses(id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((res: any) => {
          this.courses = res || [];
        });
    }
  }

  onCourseChange(courseId: any): void {
    this.transferForm.patchValue({ toBatchId: '' });
    this.batches = [];
    const id = Number(courseId);
    if (id) {
      this.studentService.getTransferBatches(id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((res: any) => {
          this.batches = res || [];
        });
    }
  }

  onSubmit(): void {
    if (this.transferForm.invalid) return;

    const payload = {
      studentCourseApplicationId: this.data.studentCourseApplicationId,
      toInstituteId: Number(this.transferForm.value.toInstituteId),
      toCourseId: Number(this.transferForm.value.toCourseId),
      toBatchId: Number(this.transferForm.value.toBatchId),
      reason: this.transferForm.value.reason
    };

    this.studentService.studentInstituteTransfer(payload)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        () => {
          this.snackBar.open('Student transferred successfully!', '', { duration: 3000 });
          this.dialogRef.close(true);
        },
        (error: any) => {
          this.snackBar.open(error?.message || 'Transfer failed!', '', { duration: 3000 });
        }
      );
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
